import React, { useState } from 'react';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { formatDuration } from 'helpers/time';
import { SourceEntry } from './recordingSources';
import styles from './AIClips.module.scss';

interface SourcePlaylistProps {
  sources: SourceEntry[];
  selectedIdx: number;
  onSelect: (idx: number) => void;
  /** Durations the reference player measured, by source index. */
  measuredDurations: Record<number, number>;
}

/**
 * Collapsible "Recordings" list under the reference player, one card per part. Renders nothing
 * for a single recording — there is nothing to switch between.
 */
const SourcePlaylist: React.FC<SourcePlaylistProps> = ({
  sources,
  selectedIdx,
  onSelect,
  measuredDurations,
}) => {
  const [open, setOpen] = useState(true);

  if (sources.length <= 1) return null;

  return (
    <div className={styles.sourcePlaylist}>
      <button
        className={styles.sourcePlaylistToggle}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span className={styles.sourcePlaylistTitle}>Recordings</span>
        <span className={styles.sourcePlaylistCount}>
          {selectedIdx + 1}/{sources.length} videos
        </span>
        {open ? <UpOutlined /> : <DownOutlined />}
      </button>

      {open && (
        <div className={styles.sourceCards}>
          {sources.map((source, idx) => {
            const isActive = idx === selectedIdx;
            // Postgres knows both already; the measured value is only a fallback for the
            // clip-record path, which carries neither.
            const duration = source.duration ?? measuredDurations[idx];
            return (
              <button
                key={source.videoUrl}
                className={`${styles.sourceCard} ${
                  isActive ? styles.sourceCardActive : ''
                }`}
                onClick={() => onSelect(idx)}
                aria-label={`Play part ${source.partNumber ?? idx + 1}`}
                aria-current={isActive}
              >
                {source.thumbnailUrl ? (
                  <img
                    src={source.thumbnailUrl}
                    alt=""
                    loading="lazy"
                    className={styles.sourceCardVideo}
                  />
                ) : (
                  <video
                    src={`${source.videoUrl}#t=0.5`}
                    preload="metadata"
                    muted
                    className={styles.sourceCardVideo}
                  />
                )}
                <span className={styles.sourceCardLabel}>
                  Part {source.partNumber ?? idx + 1}
                </span>
                {duration != null && (
                  <span className={styles.durationBadge}>
                    {formatDuration(duration)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SourcePlaylist;
